import { buildTaskListQuery } from "./taskListQuery";
import { AppEvents } from "../../events/events";
import { PagedResult } from "../../interfaces/PagedResult";
import InterimTemplateRequest from "../../interfaces/task/InterimTemplateRequest";
import InterimTemplateResponse from "../../interfaces/task/InterimTemplateResponse";
import NewInterimTemplate from "../../interfaces/task/NewInterimTemplate";
import NewTask, { PublishTaskResDto } from "../../interfaces/task/NewTask";
import Task from "../../interfaces/task/Task"; // Import your Task interface
import TaskFilters from "../../interfaces/task/TaskFilters";
import TaskList from "../../interfaces/task/TaskList";
import TaskRequest from "../../interfaces/task/TaskRequest";

import { TemplateRelationshipResponse } from "../../interfaces/taskRelationship/taskRelationship";
import { CreateAdministrativeTransactionRequest } from "../../interfaces/adminRequests/CreateAdministrativeTransactionRequest";
import { ApiResponse } from "../../interfaces/genericResponse/GenericResponse";
import { apiPost } from "../../utilities/api-client/apiRequest";
import { parseUtcTimestamp } from "../../utilities/utcDateTime";

type TaskFiltersWithIssues = TaskFilters & { hasIssues?: boolean };

export class TaskLookupError extends Error {
  constructor(public readonly status: number, public readonly code?: string) {
    super("Task lookup failed.");
    this.name = "TaskLookupError";
  }
}

/**
 * Appends the optional inclusive task due-date range expected by the API.
 * Values should be date-only strings in YYYY-MM-DD format to avoid timezone shifts.
 */
function appendDateRangeParams(
  queryParams: URLSearchParams,
  filters?: TaskFiltersWithIssues | null,
): void {
  if (filters?.dateRangeStart) {
    queryParams.set("dateRangeStart", filters.dateRangeStart);
  }

  if (filters?.dateRangeEnd) {
    queryParams.set("dateRangeEnd", filters.dateRangeEnd);
  }
}

// Function to fetch a task by its ID
export async function getTaskById(
  taskId: string,
  authToken: string,
): Promise<Task | null> {
  try {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/tasks/${taskId}`;

    const response = await fetch(apiUrl, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (response.status === 401 || response.status === 403) {
      const errorBody = await response.json().catch(() => null);
      throw new TaskLookupError(response.status, errorBody?.errorCode);
    }

    if (!response.ok) {
      // For other non-OK responses, return null.
      return null;
    }

    const taskData = await response.json();

    // Map the task data to your Task interface
    const task: Task = {
      id: taskData.id,
      friendlyId: taskData.friendlyId,
      title: taskData.title,
      assignees: taskData.assignees,
      participant: taskData.participant ? {
        ...taskData.participant,
        creationDate: new Date(taskData.participant.creationDate),
        linkageDate: new Date(taskData.participant.linkageDate),
      } : null,
      description: taskData.description,
      isReady: taskData.isTaskReady,
      information: taskData.information ? taskData.information : undefined,
      organization: taskData.organization,
      taskType: {
        id: taskData.type.id,
        type: taskData.type.name,
      },
      taskOrganization: {
        id: taskData.taskOrg.id,
        type: taskData.taskOrg.name,
      },
      subType: {
        id: taskData.subType.id,
        type: taskData.subType.name,
      },
      startDate: new Date(taskData.startDate),
      endDate: new Date(taskData.endDate),
      effectiveEndDate: taskData.effectiveEndDate ? new Date(taskData.effectiveEndDate) : undefined,
      completionTime: parseUtcTimestamp(taskData.completionTime) ?? undefined,
      progress: taskData.progress,
      status: taskData.taskStatus,
      hasIssues: Boolean(taskData.hasIssues),
      trainingCourseId: taskData.trainingCourseId,
      isResourceFollowUp: taskData.isResourceFollowUp === true,
      taskContext: taskData.taskContext,
      visibilityMode: taskData.visibilityMode,
      subjectEmployee: taskData.subjectEmployee ?? null,
      priority: taskData.priority,
      tags: taskData.tags,
      justificationReason: taskData.justificationReason,
      justificationType: taskData.justificationType,
      justificationSubmissionDate: taskData.justificationSubmissionDate,
      justificationSubmittedBy: taskData.justificationSubmittedBy,
      administrativeTransactionRequests:
        taskData.administrativeTransactionRequests?.map((req: any) => ({
          id: req.id,
          requesterName: req.requesterName,
          approverName: req.approverName,
          transactionType: req.transactionType,
          status: req.status,
          requestTime: req.requestTime,
          transactionReasonText: req.transactionReasonText,
        })), // Add other necessary properties
      meetingsCount: taskData.meetingCount,
      attachmentCount: taskData.attachmentCount,
      pendingSignatureCount: taskData.pendingSignatureCount,
      commentsCount: taskData.commentsCount,
      canExtent: taskData.canExtent,
      taskTemplateId: taskData.taskTemplateId,
      relationships: taskData.relationships || [], // Add relationships here
      extensionHistory: taskData.extensionHistory,
      extensionCount: taskData.extensionCount,
      currentTaskDelay: taskData.currentTaskDelay ?? null,
      isPendingExtension: taskData.isPendingExtension,
      capabilities: taskData.capabilities,
      workflowBlueprint: taskData.workflowBlueprint || [],
      meetings: taskData.meetings || [], // Include meetings array from API
    };

    return task;
  } catch (error) {
    const status = error instanceof TaskLookupError ? error.status : "network-or-client-error";
    console.error("Task read failed.", { status });
    throw error; // Propagate the error so the component can catch it.
  }
}

export async function lookupTask(
  friendlyId: string,
  authToken: string,
): Promise<Task | null> {
  try {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/tasks/lookup/${friendlyId}`;

    const response = await fetch(apiUrl, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (response.status === 401 || response.status === 403) {
      const errorBody = await response.json().catch(() => null);
      throw new TaskLookupError(response.status, errorBody?.errorCode);
    }

    if (!response.ok) {
      // For other non-OK responses, return null.
      return null;
    }

    const taskData = await response.json();

    // Map the task data to your Task interface
    const task: Task = {
      id: taskData.id,
      friendlyId: taskData.friendlyId,
      title: taskData.title,
      assignees: taskData.assignees,
      participant: taskData.participant ? {
        ...taskData.participant,
        creationDate: new Date(taskData.participant.creationDate),
        linkageDate: new Date(taskData.participant.linkageDate),
      } : null,
      subjectEmployee: taskData.subjectEmployee ?? null,
      description: taskData.description,
      isReady: taskData.isTaskReady,
      information: taskData.information ? taskData.information : undefined,
      organization: taskData.organization,
      taskType: {
        id: taskData.type.id,
        type: taskData.type.name,
      },
      taskOrganization: {
        id: taskData.taskOrg.id,
        type: taskData.taskOrg.name,
      },
      subType: {
        id: taskData.subType.id,
        type: taskData.subType.name,
      },
      startDate: new Date(taskData.startDate),
      endDate: new Date(taskData.endDate),
      effectiveEndDate: new Date(taskData.effectiveEndDate),
      completionTime: parseUtcTimestamp(taskData.completionTime) ?? undefined,
      progress: taskData.progress,
      status: taskData.taskStatus,
      hasIssues: Boolean(taskData.hasIssues),
      trainingCourseId: taskData.trainingCourseId,
      isResourceFollowUp: taskData.isResourceFollowUp === true,
      taskContext: taskData.taskContext,
      visibilityMode: taskData.visibilityMode,
      priority: taskData.priority,
      tags: taskData.tags,
      justificationReason: taskData.justificationReason,
      justificationType: taskData.justificationType,
      justificationSubmissionDate: taskData.justificationSubmissionDate,
      justificationSubmittedBy: taskData.justificationSubmittedBy,
      administrativeTransactionRequests:
        taskData.administrativeTransactionRequests?.map((req: any) => ({
          id: req.id,
          requesterName: req.requesterName,
          approverName: req.approverName,
          transactionType: req.transactionType,
          status: req.status,
          requestTime: req.requestTime,
          transactionReasonText: req.transactionReasonText,
        })), // Add other necessary properties
      meetingsCount: taskData.meetingCount,
      attachmentCount: taskData.attachmentCount,
      pendingSignatureCount: taskData.pendingSignatureCount,
      commentsCount: taskData.commentsCount,
      canExtent: taskData.canExtent,
      taskTemplateId: taskData.taskTemplateId,
      relationships: taskData.relationships || [], // Add relationships here
      extensionHistory: taskData.extensionHistory,
      extensionCount: taskData.extensionCount,
      currentTaskDelay: taskData.currentTaskDelay ?? null,
      isPendingExtension: taskData.isPendingExtension,
      signed: taskData.signed,
      capabilities: taskData.capabilities,
      workflowBlueprint: taskData.workflowBlueprint || [],
      meetings: taskData.meetings || [], // Include meetings array from API
    };

    return task;
  } catch (error) {
    const status = error instanceof TaskLookupError ? error.status : "network-or-client-error";
    console.error("Task lookup failed.", { status });
    throw error; // Propagate the error so the component can catch it.
  }
}

/* Fetches *organization-wide tasks* from the API, which the server will only return
 * if the user making the request is OWNER or ADMIN. All filtering is optional and
 * done on the server side.
 */
export async function getOrganizationTasks(
  authToken: string,
  page: number = 1,
  pageSize: number = 100,
  filters?: TaskFiltersWithIssues,
): Promise<{ tasks: TaskList[]; totalCount: number } | null> {
  try {
    // Build query parameters
    const queryParams = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    // If we have filters, append them
    if (filters) {
      if (filters.status && filters.status.length > 0) {
        filters.status.forEach((s) => queryParams.append("status", s));
      }
      if (filters.type && filters.type.length > 0) {
        filters.type.forEach((t) => queryParams.append("type", t));
      }
      if (filters.date && filters.date.length > 0) {
        filters.date.forEach((d) => queryParams.append("date", d));
      }
      appendDateRangeParams(queryParams, filters);
      if (filters.assignees && filters.assignees.length > 0) {
        filters.assignees.forEach((a) => queryParams.append("assignee", a));
      }
      if (filters.sortKey) {
        queryParams.append("sortKey", filters.sortKey);
      }
      if (filters.sortDirection) {
        queryParams.append("sortDirection", filters.sortDirection);
      }
      if (filters.participants && filters.participants.length > 0) {
        filters.participants.forEach((p) =>
          queryParams.append("participant", p),
        );
      }
      if (filters.employeeSubjects && filters.employeeSubjects.length > 0) {
        filters.employeeSubjects.forEach((employeeId) =>
          queryParams.append("employeeSubject", employeeId),
        );
      }
      if (filters.tags && filters.tags.length > 0) {
        filters.tags.forEach((t) => queryParams.append("tags", t));
      }
      if (filters.excludeTags && filters.excludeTags.length > 0) {
        filters.excludeTags.forEach((t) =>
          queryParams.append("excludeTags", t),
        );
      }
      if (filters.excludeDate && filters.excludeDate.length > 0) {
        filters.excludeDate.forEach((d) =>
          queryParams.append("excludeDate", d),
        );
      }
      if (filters.excludeStatus && filters.excludeStatus.length > 0) {
        filters.excludeStatus.forEach((s) =>
          queryParams.append("excludeStatus", s),
        );
      }
      if (filters.excludeType && filters.excludeType.length > 0) {
        filters.excludeType.forEach((t) =>
          queryParams.append("excludeType", t),
        );
      }

      queryParams.append("performanceDiagnostics", "true")
      queryParams.append("projectionMode", "full")

      if (filters.automationIds && filters.automationIds.length > 0) {
        filters.automationIds.forEach((a) => queryParams.append("automation", a));
      }
      if (filters.excludeAutomationIds && filters.excludeAutomationIds.length > 0) {
        filters.excludeAutomationIds.forEach((a) => queryParams.append("excludeAutomation", a));
      }
      if (filters.taskDelayFilter && filters.taskDelayFilter !== "all") {
        queryParams.set("taskDelayFilter", filters.taskDelayFilter);
      }
      if (typeof filters.hasIssues === "boolean") {
        queryParams.set("hasIssues", String(filters.hasIssues));
      }
      if (filters.taskContext) {
        queryParams.set("taskContext", filters.taskContext);
      }
      if (filters.leaveImpactEmployeeId && filters.leaveImpactStart &&
          filters.leaveImpactEnd && filters.leaveImpactAsOf) {
        queryParams.set("leaveImpactEmployeeId", filters.leaveImpactEmployeeId);
        queryParams.set("leaveImpactStart", filters.leaveImpactStart);
        queryParams.set("leaveImpactEnd", filters.leaveImpactEnd);
        queryParams.set("leaveImpactAsOf", filters.leaveImpactAsOf);
      }
    }

    // Adjust this endpoint to match your .NET Core route for "GetTasksInOrganization"
    // e.g. "/tasks/organization" or something similar
    const apiUrl = `${
      process.env.REACT_APP_API_BASE_URL
    }/tasks/all?${queryParams.toString()}`;

    const response = await fetch(apiUrl, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      // If server returns 401, 403, or other error, handle accordingly
      return null;
    }

    const res = await response.json();

    // Map the task data to your TaskList interface
    const tasks: TaskList[] = res.tasks.map((taskData: any) => ({
      id: taskData.id,
      friendlyId: taskData.friendlyId,
      title: taskData.title,
      assignees: taskData.assignees,
      description: taskData.description,
      organization: taskData.organization,
      participant: taskData.participant ? {
        ...taskData.participant,
        creationDate: new Date(taskData.participant.creationDate),
        linkageDate: new Date(taskData.participant.linkageDate),
      } : null,
      subjectEmployee: taskData.subjectEmployee ?? null,
      taskType: taskData.type,
      hasAttachments: taskData.hasAttachments,
      taskOrganization: taskData.taskOrg,
      subType: {
        id: taskData.subType.id,
        type: taskData.subType.name,
      },
      startDate: new Date(taskData.startDate),
      endDate: new Date(taskData.endDate),
      completionTime: parseUtcTimestamp(taskData.completionTime) ?? undefined,
      progress: 100, // Adjust if you have a real progress field
      status: taskData.taskStatus,
      hasIssues: Boolean(taskData.hasIssues),
      trainingCourseId: taskData.trainingCourseId,
      isResourceFollowUp: taskData.isResourceFollowUp === true,
      taskContext: taskData.taskContext,
      visibilityMode: taskData.visibilityMode,
      priority: "LOW", // Adjust if needed
      assigneeRoleName: taskData.assigneeRoleName,
      fromTemplate: taskData.fromTemplate,
      tags: taskData.tags,
      lastActivityDate: parseUtcTimestamp(taskData.lastActivityDate) ?? undefined,
      currentTaskDelay: taskData.currentTaskDelay ?? null,
      // Add or map any other properties you need
    }));

    return {
      tasks,
      totalCount: res.totalCount,
    };
  } catch (error) {
    console.error("Error fetching organization tasks:", error);
    return null;
  }
}

// Example only: adjust the path & parameters as needed
export const fetchOrganizationTasksWithCache = async (
  authToken: string,
  cacheKey: string = "organization_tasks",
  page = 1,
  pageSize = 25,
  filters?: TaskFiltersWithIssues,
  cacheTTL = 2 * 60 * 1000,
): Promise<{ tasks: TaskList[]; totalCount: number } | null> => {
  // Build query params
  const queryParams = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString(),
  });

  if (filters) {
    if (filters.status && filters.status.length > 0) {
      filters.status.forEach((s) => queryParams.append("status", s));
    }
    if (filters.type && filters.type.length > 0) {
      filters.type.forEach((t) => queryParams.append("type", t));
    }
    if (filters.date && filters.date.length > 0) {
      filters.date.forEach((d) => queryParams.append("date", d));
    }
    appendDateRangeParams(queryParams, filters);
    if (filters.assignees && filters.assignees.length > 0) {
      filters.assignees.forEach((a) => queryParams.append("assignee", a));
    }
    if (filters.participants && filters.participants.length > 0) {
      filters.participants.forEach((p) => queryParams.append("participant", p));
    }
    if (filters.employeeSubjects && filters.employeeSubjects.length > 0) {
      filters.employeeSubjects.forEach((employeeId) =>
        queryParams.append("employeeSubject", employeeId),
      );
    }
  }

  queryParams.append("performanceDiagnostics", "true")
  queryParams.append("projectionMode", "full")
  

  if (filters?.sortKey) {
    queryParams.append("sortKey", filters.sortKey);
  }
  if (filters?.sortDirection) {
    queryParams.append("sortDirection", filters.sortDirection);
  }



  // Fetch from server
  const tasks: any = await getOrganizationTasks(
    authToken,
    page,
    pageSize,
    filters,
  ).then((t: any) => {
    return { tasks: t?.tasks, totalCount: t?.totalCount };
  });


  if (tasks) {
    return tasks;
  } else {
    return null;
  }
};

// Function to fetch all tasks for logged-in User
export async function getTasks(
  authToken: string,
  userId?: string,
  page: number = 1,
  pageSize: number = 100, // Default page size
  filters?: TaskFiltersWithIssues | null,
  sortBy: string = "",
  sortDesc: boolean = false,
  viewAll: boolean = false,
  includePendingApprovals: boolean = false,
  searchTerm?: string,
  signal?: AbortSignal,
  sortDescriptors: string[] = [],
): Promise<{ tasks: TaskList[]; totalCount: number } | null> {
  try {
    const queryParams = buildTaskListQuery(filters, sortBy, sortDesc, sortDescriptors, viewAll, includePendingApprovals, searchTerm);
    queryParams.set("page", String(page));
    queryParams.set("pageSize", String(pageSize));

    queryParams.append("performanceDiagnostics", "true")
    queryParams.append("projectionMode", "full")

    console.log("Query params for get tasks: ", queryParams.toString());

    let apiUrl = `${
      process.env.REACT_APP_API_BASE_URL
    }/tasks?${queryParams.toString()}`;

    // If a userId is specified, use the user-specific endpoint
    if (userId) {
      apiUrl = `${
        process.env.REACT_APP_API_BASE_URL
      }/tasks/user/${userId}?${queryParams.toString()}`;
    }

    const response = await fetch(apiUrl, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      signal,
    });

    if (!response.ok) {
      // Handle non-OK responses, e.g., 404 for not found
      return null;
    }

    const res = await response.json();

    // Map the task data to your Task interface
    const tasks: TaskList[] = res.tasks.map((taskData: any) => ({
      id: taskData.id,
      friendlyId: taskData.friendlyId,
      title: taskData.title,
      assignees: taskData.assignees,
      description: taskData.description,
      organization: taskData.organization,
      participant: taskData.participant ? {
        ...taskData.participant,
      } : null,
      subjectEmployee: taskData.subjectEmployee ?? null,
      taskType: taskData.type,
      hasAttachments: taskData.hasAttachments,
      attachmentCount: taskData.attachmentCount,
      pendingSignatureCount: taskData.pendingSignatureCount,
      taskOrganization: taskData.taskOrg,
      subType: {
        id: taskData.subType.id,
        type: taskData.subType.name,
      },
      startDate: new Date(taskData.startDate),
      endDate: new Date(taskData.endDate),
      effectiveEndDate: new Date(taskData.effectiveEndDate),
      calendarDueDate: (taskData.effectiveEndDate || taskData.endDate)?.slice(0, 10),
      completionTime: parseUtcTimestamp(taskData.completionTime) ?? undefined,
      progress: 100,
      status: taskData.taskStatus,
      hasIssues: Boolean(taskData.hasIssues),
      trainingCourseId: taskData.trainingCourseId,
      isResourceFollowUp: taskData.isResourceFollowUp === true,
      taskContext: taskData.taskContext,
      visibilityMode: taskData.visibilityMode,
      priority: "LOW",
      assigneeRoleName: taskData.assigneeRoleName,
      fromTemplate: taskData.fromTemplate,
      tags: taskData.tags,
      lastActivityDate: parseUtcTimestamp(taskData.lastActivityDate) ?? undefined,
      currentTaskDelay: taskData.currentTaskDelay ?? null,
      // Add other necessary properties
    }));

    console.log(tasks);
    return { tasks: tasks, totalCount: res.totalCount };
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw error;
    }

    console.error("Error fetching task:", error);
    return null;
  }
}

export interface TaskWorkspaceCounts {
  Client: number;
  Employee: number;
}

// A sidebar badge describes only the effective employee's open tasks, not team work.
export async function getOwnOpenEmployeeTaskCount(authToken: string, userId: string, signal?: AbortSignal): Promise<number> {
  const params = new URLSearchParams({ page: "1", pageSize: "1", taskContext: "Employee", assignee: userId, viewAll: "false", includePendingApprovals: "true" });
  ["DELETED", "COMPLETED", "CLOSED"].forEach(status => params.append("excludeStatus", status));
  const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tasks/user/${encodeURIComponent(userId)}?${params}`, {
    headers: { Authorization: `Bearer ${authToken}` }, signal,
  });
  if (!response.ok) throw new Error("Employee task count unavailable.");
  const body = await response.json();
  if (!Number.isSafeInteger(body.totalCount) || body.totalCount < 0) throw new Error("Invalid employee task count.");
  return body.totalCount;
}

export async function getTaskWorkspaceCounts(
  authToken: string,
  userId?: string,
  signal?: AbortSignal,
): Promise<TaskWorkspaceCounts> {
  const loadCount = async (taskContext: "Client" | "Employee") => {
    const queryParams = new URLSearchParams({
      page: "1",
      pageSize: "1",
      taskContext,
      sortBy: "enddate",
      sortDesc: "false",
      viewAll: "false",
      includePendingApprovals: "true",
    });
    ["DELETED", "COMPLETED", "CLOSED"].forEach((status) =>
      queryParams.append("excludeStatus", status),
    );
    const endpoint = userId ? `/tasks/user/${userId}` : "/tasks";
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}${endpoint}?${queryParams.toString()}`,
      { headers: { Authorization: `Bearer ${authToken}` }, signal },
    );
    if (!response.ok) return 0;
    const body = await response.json();
    return Number(body.totalCount) || 0;
  };

  const [Client, Employee] = await Promise.all([
    loadCount("Client"),
    loadCount("Employee"),
  ]);
  return { Client, Employee };
}

// utils/taskCache.ts

export const fetchTasksWithCache = async (
  authToken: string,
  userId: string,
  isCurrentlyLoggedInUser: boolean = true,
  cacheTTL: number = 2 * 60 * 1000, // e.g. 2 minutes
) => {
  const cacheKey = `tasks_${isCurrentlyLoggedInUser ? "ADMIN_" : ""}${userId}`;
  const cached = localStorage.getItem(cacheKey);

  if (cached) {
    try {
      const parsed = JSON.parse(cached) as {
        tasks: TaskList[];
        lastRetrieved: number;
      };

      const isFresh = Date.now() - parsed.lastRetrieved < cacheTTL;
      if (isFresh) {
        // Return the cached tasks if still valid
        return parsed.tasks;
      }
    } catch (error) {
      // If JSON parse fails, we'll just fetch new data
      console.error("Error parsing cache for", userId, error);
    }
  }

  let tasks;

  // If nothing is cached or it’s stale, fetch from the server
  if (isCurrentlyLoggedInUser) {
    tasks = await getTasks(authToken).then((t) => {
      return t?.tasks;
    });
  } else {
    console.log("CALLING TASK FROM SPECIFIC USER");
    tasks = await getTasks(authToken, userId).then((t) => {
      return t?.tasks;
    });
  }
  localStorage.setItem(
    cacheKey,
    JSON.stringify({
      tasks,
      lastRetrieved: Date.now(),
    }),
  );
  return tasks;
};

// Function to fetch tasks for a participant
export async function getTasksForParticipant(
  authToken: string,
  participantId: string,
  viewAll: boolean,
  page: number = 1,
  pageSize: number = 10,
  status?: string[],
  hasIssues?: boolean,
): Promise<PagedResult<TaskList> | null> {
  try {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      viewAll: viewAll.toString(),
    });

    if (typeof hasIssues === "boolean") {
      queryParams.set("hasIssues", String(hasIssues));
    }

    // Add status parameters if provided
    if (status && status.length > 0) {
      status.forEach((s) => {
        queryParams.append("status", s);
      });
    } else {
    }

    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/tasks/participant/${participantId}?${queryParams.toString()}`;

    // Debug: Log the URL to verify status parameters are included
    console.log("getTasksForParticipant API URL:", apiUrl);
    console.log("getTasksForParticipant Query Params:", queryParams.toString());

    const response = await fetch(apiUrl, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      // Handle non-OK responses, e.g., 404 for not found
      return null;
    }

    const res = await response.json();

    // Map the task data to your Task interface
    const tasks: TaskList[] = res.items.map((taskData: any) => ({
      id: taskData.id,
      friendlyId: taskData.friendlyId,
      title: taskData.title,
      assignees: taskData.assignees,
      description: taskData.description,
      organization: taskData.organization,
      participant: taskData.participant ? {
        ...taskData.participant,
        creationDate: new Date(taskData.participant.creationDate),
        linkageDate: new Date(taskData.participant.linkageDate),
      } : null,
      subjectEmployee: taskData.subjectEmployee ?? null,
      taskType: taskData.type,
      hasAttachments: taskData.hasAttachments,
      taskOrganization: taskData.taskOrg,
      subType: {
        id: taskData.subType.id,
        type: taskData.subType.name,
      },
      startDate: new Date(taskData.startDate),
      endDate: new Date(taskData.endDate),
      completionTime: parseUtcTimestamp(taskData.completionTime) ?? undefined,
      progress: 100,
      status: taskData.taskStatus,
      hasIssues: Boolean(taskData.hasIssues),
      trainingCourseId: taskData.trainingCourseId,
      isResourceFollowUp: taskData.isResourceFollowUp === true,
      taskContext: taskData.taskContext,
      visibilityMode: taskData.visibilityMode,
      priority: "LOW",
      assigneeRoleName: taskData.assigneeRoleName,
      fromTemplate: taskData.fromTemplate,
      automationId: taskData.automationId ?? null,
      automationTitle: taskData.automationTitle ?? null,
      automationType: taskData.automationType ?? null,
      automationGroupId: taskData.automationGroupId ?? null,
      automationGroupName: taskData.automationGroupName ?? null,
      tags: taskData.tags,
      lastActivityDate: parseUtcTimestamp(taskData.lastActivityDate) ?? undefined,
      currentTaskDelay: taskData.currentTaskDelay ?? null,
      // Add other necessary properties
    }));

    return {
      items: tasks,
      totalCount: res.totalCount,
    };
  } catch (error) {
    // Handle network errors or other exceptions
    console.error("Error fetching task:", error);
    return null;
  }
}

// Function to fetch participant by task ID
// Calls GET /api/task/participant?taskId={taskId}
export async function getTaskParticipant(
  taskId: string,
  authToken: string,
): Promise<{
  id: number;
  firstName: string;
  lastName: string;
  deactivated: boolean;
} | null> {
  try {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/task/participant?taskId=${taskId}`;

    const response = await fetch(apiUrl, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (
      response.status === 400 ||
      response.status === 403 ||
      response.status === 404
    ) {
      return null;
    }

    if (!response.ok) {
      return null;
    }

    const participantData = await response.json();
    return {
      id: participantData.id,
      firstName: participantData.firstName || "",
      lastName: participantData.lastName || "",
      deactivated: participantData.deactivated || false,
    };
  } catch (error) {
    console.error("Error fetching task participant:", error);
    return null;
  }
}

export async function publishTask(
  authToken: string,
  taskRequest: NewTask,
): Promise<ApiResponse<PublishTaskResDto>> {
  const apiUrl = `/tasks`;

  const requestBody: TaskRequest = {
    title: taskRequest.title,
    description: taskRequest.description,
    organizationId: 1,
    workflowGroup: taskRequest.workflowGroup,
    taskOrganization: Number(taskRequest.taskOrganization),
    taskType: Number(taskRequest.taskType),
    taskSubType: Number(taskRequest.taskSubType),
    participant: taskRequest.participant ? Number(taskRequest.participant) : undefined,
    templateTaskId: undefined,
    requiredActions: undefined,
    requiredFiles: taskRequest.requiredFiles,
    assignee: taskRequest.assignee || "",
    coAssignee: taskRequest.coAssignee,
    endDate: taskRequest?.dueDate || Date.now().toString(),
    trainingCourseId: taskRequest.taskContext === "Employee" ? taskRequest.trainingCourseId ?? null : null,
    taskContext: taskRequest.taskContext,
    employeeSubjectId: taskRequest.employeeSubjectId,
    visibilityMode: taskRequest.visibilityMode ?? "Standard",
  };

  const res = await apiPost<ApiResponse<PublishTaskResDto>>(
    apiUrl,
    requestBody,
    authToken,
  );
  if (res.success) AppEvents.emit("UPDATE_EMPLOYEE_TASK_COUNT");
  return res;
}

// export async function publishTask(
//   authToken: string,
//   taskRequest: NewTask
// ): Promise<ApiResponse<PublishTaskResDto>> {
//   const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/tasks`;

//   // Converet NewTask type to TaskRequest type
//   const requestBody: TaskRequest = {
//     title: taskRequest.title,
//     description: taskRequest.description,
//     organizationId: 1,
//     workflowGroup: taskRequest.workflowGroup,
//     taskOrganization: Number(taskRequest.taskOrganization),
//     taskType: Number(taskRequest.taskType),
//     taskSubType: Number(taskRequest.taskSubType),
//     participant: Number(taskRequest.participant),
//     templateTaskId: undefined,
//     requiredActions: undefined,
//     requiredFiles: taskRequest.requiredFiles,
//     assignee: taskRequest.assignee || "",
//     coAssignee: taskRequest.coAssignee,
//     endDate: taskRequest?.dueDate || Date.now().toString(),
//   };

//   try {
//     const response = await fetch(apiUrl, {
//       method: "POST",
//       headers: {
//         Authorization: `Bearer ${authToken}`,
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify(requestBody),
//     });

//     if (!response.ok) {
//       console.log(response.json());
//       return null;
//     }

//     const taskId = await response.json();

//     return taskId;
//   } catch (error) {
//     console.error("Error posting comment", error);
//     return null;
//   }
// }

export async function publishTemplateInterimTask(
  authToken: string,
  taskRequest: NewTask,
  orgId: number,
): Promise<number | null> {
  const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/tasks/fromTemplate`;

  // Converet NewTask type to TaskRequest type
  const requestBody: TaskRequest = {
    title: taskRequest.title,
    description: taskRequest.description,
    organizationId: orgId,
    workflowGroup: taskRequest.workflowGroup,
    taskOrganization: Number(taskRequest.taskOrganization),
    taskType: Number(taskRequest.taskType),
    taskSubType: Number(taskRequest.taskSubType),
    participant: taskRequest.participant ? Number(taskRequest.participant) : undefined,
    templateTaskId: taskRequest.templateTaskId,
    requiredActions: undefined,
    requiredFiles: taskRequest.requiredFiles,
    assignee: taskRequest.assignee || "",
    coAssignee: taskRequest.coAssignee,
    endDate: taskRequest?.dueDate || Date.now().toString(),
    trainingCourseId: taskRequest.taskContext === "Employee" ? taskRequest.trainingCourseId ?? null : null,
    taskContext: taskRequest.taskContext,
    employeeSubjectId: taskRequest.employeeSubjectId,
    visibilityMode: taskRequest.visibilityMode,
  };

  const fallback = "Failed to create task. Please try again.";
  let response: Response;
  try {
    response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

  } catch {
    throw new Error(fallback);
  }

  if (!response.ok) {
    // Only expected client errors may carry guidance. Never expose server error
    // bodies, HTML proxy pages, or request/credential details through the toast.
    let message = fallback;
    if ([400, 403, 404, 409, 422].includes(response.status) &&
        !response.headers?.get("content-type")?.toLowerCase().includes("text/html")) {
      try {
        const text = (await response.text()).trim();
        let body: unknown = text;
        try { body = JSON.parse(text); } catch { /* The API also returns plain text. */ }
        const candidate = typeof body === "string" ? body :
          body && typeof body === "object" ?
            (body as { message?: unknown; Message?: unknown }).message ??
            (body as { Message?: unknown }).Message : undefined;
        if (typeof candidate === "string" && candidate.trim().length > 0 &&
            candidate.length <= 500 && !/[<>]/.test(candidate) &&
            !candidate.split("").some(character => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)) {
          message = candidate.trim();
        }
      } catch { /* An unreadable response keeps the safe fallback. */ }
    }
    throw new Error(message);
  }

  try {
    return await response.json();
  } catch {
    throw new Error(fallback);
  }
}

export async function publishInterimTemplate(
  authToken: string,
  taskRequest: NewInterimTemplate,
): Promise<number | null> {
  const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/tasks/template/interim`;

  // Converet NewTask type to TaskRequest type
  const requestBody: InterimTemplateRequest = {
    title: taskRequest.title,
    description: taskRequest.description,
    information: taskRequest.information,
    organizationId: 1,
    workflowGroup: taskRequest.workflowGroup,
    workflowDefinitionId: taskRequest.workflowDefinitionId ?? undefined,
    taskOrganization: Number(taskRequest.taskOrganization),
    taskType: Number(taskRequest.taskType),
    taskSubType: Number(taskRequest.taskSubType),
    participant: taskRequest.participant ? Number(taskRequest.participant) : undefined,
    daysFromCreationDueDate: Number(taskRequest.daysFromCreationDueDate),
    templateTaskId: undefined,
    requiredActions: undefined,
    requiredFiles: taskRequest.requiredFiles,
    assignee: taskRequest.assignee || "",
    coAssignee: taskRequest.coAssignee || "",
    endDate: taskRequest?.dueDate || Date.now().toString(),
    tags: taskRequest.tags ?? [],
    waiverIds: taskRequest.waiverIds ?? [],
    automationGroupId: taskRequest.automationGroupId ?? null,
    participantIntakeScope: taskRequest.participantIntakeScope ?? "All",
    trainingCourseId: taskRequest.taskContext === "Employee" ? taskRequest.trainingCourseId ?? null : null,
    taskContext: taskRequest.taskContext ?? "Client",
    visibilityMode: taskRequest.visibilityMode ?? "Standard",
    visibilityRules: taskRequest.visibilityRules ?? [],
  };

  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      console.log(response.json());
      return null;
    }

    const taskId = await response.json();

    return taskId;
  } catch (error) {
    console.error("Error posting interim template task", error);
    return null;
  }
}

export async function updateInterimTemplate(
  authToken: string,
  taskRequest: NewInterimTemplate,
): Promise<number | null> {
  const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/tasks/template/interim`;

  // Convert NewInterimTemplate to whatever your .NET endpoint expects
  const requestBody: InterimTemplateRequest = {
    id: taskRequest?.id,
    title: taskRequest.title,
    description: taskRequest.description,
    information: taskRequest.information,
    organizationId: 1,
    workflowGroup: taskRequest.workflowGroup,
    taskOrganization: Number(taskRequest.taskOrganization),
    taskType: Number(taskRequest.taskType),
    taskSubType: Number(taskRequest.taskSubType),
    participant: taskRequest.participant ? Number(taskRequest.participant) : undefined,
    daysFromCreationDueDate: Number(taskRequest.daysFromCreationDueDate),
    templateTaskId: undefined,
    requiredActions: undefined,
    requiredFiles: taskRequest.requiredFiles,
    assignee: taskRequest.assignee || "",
    coAssignee: taskRequest.coAssignee,
    endDate: taskRequest?.dueDate || Date.now().toString(),

    // Pass along interim-only scope fields.
    tags: taskRequest.tags ?? [],
    waiverIds: taskRequest.waiverIds,
    automationGroupId: taskRequest.automationGroupId,
    trainingCourseId: taskRequest.taskContext === "Employee" ? taskRequest.trainingCourseId ?? null : null,
    taskContext: taskRequest.taskContext,
    visibilityMode: taskRequest.visibilityMode,
    visibilityRules: taskRequest.visibilityRules,
  };

  try {
    const response = await fetch(apiUrl, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      console.log(await response.json());
      return null;
    }

    const json = await response.json();
    return json.result;
  } catch (error) {
    console.error("Error Updating InterimTemplate", error);
    return null;
  }
}

export async function getInterimTemplates(
  authToken: string,
): Promise<InterimTemplateResponse[] | null> {
  const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/tasks/template/interim`;

  try {
    const response = await fetch(apiUrl, {
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      console.log(response.json());
      return null;
    }

    const templates = await response.json();
    // Converet NewTask type to TaskRequest type
    const templateInterims: InterimTemplateResponse[] = templates.map(
      (taskRequest: InterimTemplateResponse) => ({
        id: taskRequest.id,
        title: taskRequest.title,
        description: taskRequest.description,
        information: taskRequest.information,
        daysFromCreationDueDate: Number(taskRequest.daysFromCreationDueDate),
        // @TODO: Update this for post-BLOSSOM work
        organizationId: 1,
        taskOrganization: Number(taskRequest.taskOrganization),
        taskType: Number(taskRequest.taskType),
        taskSubType: Number(taskRequest.taskSubType),
        requiredFiles: taskRequest.requiredFiles,
        tags: taskRequest.tags,
        waiverFamilies: taskRequest.waiverFamilies ?? [],
        trainingCourseId: taskRequest.taskContext === "Employee" ? taskRequest.trainingCourseId ?? null : null,
    taskContext: taskRequest.taskContext ?? "Client",
        visibilityMode: taskRequest.visibilityMode ?? "Standard",
        visibilityRules: taskRequest.visibilityRules ?? [],
      }),
    );
    return templateInterims;
  } catch (error) {
    console.error("Error posting comment", error);
    return null;
  }
}

export async function deleteTaskById(
  authToken: string,
  taskId: string,
): Promise<number | null> {
  const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/tasks/${taskId}`;

  try {
    const response = await fetch(apiUrl, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      console.log(response.json());
      return null;
    }

    AppEvents.emit("UPDATE_EMPLOYEE_TASK_COUNT");
    return 1; // success
  } catch (error) {
    console.error("Error deleting task", error);
    return null;
  }
}

export async function fetchLatestTasksForUser(
  authToken: string,
  userId?: string,
): Promise<Task[] | null> {
  let apiUrl: string;
  if (userId) {
    apiUrl = `${process.env.REACT_APP_API_BASE_URL}/tasks/latest/${userId}`;
  } else {
    apiUrl = `${process.env.REACT_APP_API_BASE_URL}/tasks/latest`;
  }

  try {
    const response = await fetch(apiUrl, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    console.log(response);

    return response.json();
  } catch (err) {
    console.error(err);
    return null;
  }
}

export async function getTaskRelationships(
  authToken: string,
  taskId: number,
): Promise<TemplateRelationshipResponse[] | null> {
  try {
    // We use the 'api/tasks' route from the TasksController
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/tasks/${taskId}/relationships`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      },
    );

    if (!response.ok) {
      console.log(await response.json());
      return null;
    }

    return (await response.json()) as TemplateRelationshipResponse[];
  } catch (error) {
    console.error(`Error fetching relationships for task ${taskId}:`, error);
    return null;
  }
}

export async function RequestTaskClosureAsync(
  authToken: string,
  taskId: number,
  payload: CreateAdministrativeTransactionRequest,
): Promise<ApiResponse<number>> {
  const apiUrl: string = `/tasks/${taskId}/request-close`;

  var res = apiPost<
    ApiResponse<number>,
    CreateAdministrativeTransactionRequest
  >(apiUrl, payload, authToken);

  return res;
}
